import { PriceZoneSystem } from '@schuelerhilfe/shared/src/enums/priceZoneSystem';
import {
  buildOpeningHoursJsonLd,
  formatPriceRange,
} from './schoolDetailsHelpers';
import type { LocationDto } from '@/lib/api';
import type { BrandProfile } from '@/lib/brandProfiles';
import { resolveCenterName } from '@/lib/location/centerName';
import {
  buildAggregateRatingJsonLd,
  buildReviewListJsonLd,
  mergeJsonLdReviewItems,
} from '@/src/widgets/reviews/ReviewsJsonLd';
import type { ReviewItem, ReviewsSummary } from '@/src/widgets/reviews/types';

/**
 * Stable `@id` for a location's LocalBusiness JSON-LD node.
 *
 * `canonicalOrigin` is passed in rather than read from the environment: this
 * module is reached from a client component, where the environment is not
 * available (70.13-Runtime-Configuration). It is the brand's canonical origin,
 * the same one `<link rel="canonical">` is built from — never the host
 * production happens to be served on.
 */
export function buildLocalBusinessJsonLdId(
  location: LocationDto,
  canonicalOrigin: string,
): string {
  return `${canonicalOrigin}/nachhilfe/${location.slug}#business`;
}

/**
 * The Standort page's own URL, in the same form as its `<link rel="canonical">`
 * (trailing slash included), built from the brand's canonical origin (SH-505).
 */
export function buildLocalBusinessUrl(
  location: LocationDto,
  canonicalOrigin: string,
): string {
  return `${canonicalOrigin}/nachhilfe/${location.slug}/`;
}

/** Decimal places Google expects for `GeoCoordinates` (SH-464): ~0.1 m. */
const GEO_DECIMALS = 6;

/**
 * Rounds a plain decimal (`"51.4721075"`) half away from zero to exactly
 * `places` decimals, working on the digits themselves. `Number#toFixed` rounds
 * the nearest binary double instead, which for `51.4721075` lies just below the
 * midpoint and yields `51.472107`. Returns undefined for anything that is not a
 * plain decimal, including an empty string.
 */
export function roundDecimalString(
  value: string | number | undefined | null,
  places: number,
): string | undefined {
  if (value == null) {
    return undefined;
  }
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(String(value).trim());
  if (!match) {
    return undefined;
  }
  const [, sign, whole, fraction = ''] = match;
  const digits = (whole + fraction.padEnd(places + 1, '0').slice(0, places + 1))
    .split('')
    .map(Number);
  let carry = (digits.pop() ?? 0) >= 5;
  for (let i = digits.length - 1; carry && i >= 0; i--) {
    carry = digits[i] === 9;
    digits[i] = carry ? 0 : digits[i] + 1;
  }
  if (carry) {
    digits.unshift(1);
  }
  const rounded = digits.join('');
  return `${sign}${rounded.slice(0, -places)}.${rounded.slice(-places)}`;
}

/**
 * `GeoCoordinates` with both values at exactly six decimal places, emitted as
 * strings so the trailing zeros survive serialisation. Omitted unless both
 * coordinates are plain decimals.
 */
function buildGeoJsonLd(
  latitude: string | number | undefined | null,
  longitude: string | number | undefined | null,
): Record<string, unknown> | undefined {
  const lat = roundDecimalString(latitude, GEO_DECIMALS);
  const lng = roundDecimalString(longitude, GEO_DECIMALS);
  if (lat === undefined || lng === undefined) {
    return undefined;
  }
  return {
    '@type': 'GeoCoordinates',
    'latitude': lat,
    'longitude': lng,
  };
}

/** What the School Details widget adds to the Standort's own data. */
export interface LocalBusinessJsonLdExtras {
  /** The active brand's profile; absent for brands without one. */
  profile?: BrandProfile | undefined;
  /** The Standort's Google reviews, resolved by the backend. */
  reviews?: { summary: ReviewsSummary; items: ReviewItem[] } | undefined;
}

/**
 * Whether the node publishes the Standort's Google `aggregateRating` (SH-543).
 *
 * Off: the page's rating is the one eKomi's badge script injects as its own
 * `Organization` node (spec 60.12), and publishing Google's as well gave the
 * page two. Kept as a switch rather than deleted, because SH intends to test
 * which of the two ratings performs better.
 */
export const PUBLISH_GOOGLE_AGGREGATE_RATING: boolean = false;

/**
 * Whether the node publishes the Standort's Google `review` list.
 *
 * Off. With `PUBLISH_GOOGLE_AGGREGATE_RATING` off, this node carried reviews
 * and no rating, which is what Search Console reported on 2026-10-29 as
 * "Mehrere Rezensionen ohne aggregateRating-Objekt" across the Standort pages.
 * Google wants the `aggregateRating` on the same item as the reviews, and
 * eKomi's rating cannot serve: it is a separate `Organization` node, injected
 * client-side. Dropping the list is the side of that pair SH keeps, because
 * the rating is deliberately eKomi's (D6) and the legacy site published no
 * reviews here either — so this is SEO parity, not a regression.
 *
 * **The two switches move together.** Publishing the reviews again means
 * turning `PUBLISH_GOOGLE_AGGREGATE_RATING` back on in the same deploy, or
 * Search Console reports the page again.
 */
export const PUBLISH_GOOGLE_REVIEW_LIST: boolean = false;

/** The brand's services as a schema.org `OfferCatalog` of price-less `Offer` → `Service` entries. */
function buildOfferCatalogJsonLd(
  catalog: BrandProfile['offerCatalog'],
): Record<string, unknown> {
  return {
    '@type': 'OfferCatalog',
    'name': catalog.name,
    'itemListElement': catalog.services.map((service) => ({
      '@type': 'Offer',
      'itemOffered': {
        '@type': 'Service',
        'name': service.name,
        'description': service.description,
      },
    })),
  };
}

/**
 * Builds the LocalBusiness JSON-LD object for a location — the Standort's
 * one node, reviews included (spec 10.03.02.01.20).
 * Returns null if no location.
 */
export function buildLocalBusinessJsonLd(
  location: LocationDto | undefined,
  system: PriceZoneSystem,
  canonicalOrigin: string,
  extras: LocalBusinessJsonLdExtras = {},
): Record<string, unknown> | null {
  if (!location) {
    return null;
  }

  const { profile, reviews } = extras;
  const openingHours = buildOpeningHoursJsonLd(location.officeHours);
  const priceRange = formatPriceRange(location.priceZones, system);
  // The Standort's own Matelso label, and nothing else (SH-468): not the
  // dialable `phone`, and not the 70.07 chain's widget or Domain Setting
  // fallback — a template-wide or central hotline is not this business's
  // number. Without a label of its own, `telephone` is omitted.
  const telephone = location.matelsoPhoneLabel?.trim();
  const aggregateRating = reviews
    ? buildAggregateRatingJsonLd(reviews.summary)
    : null;
  // Same ordering, date check and cap as the Reviews widget's list, and the
  // same review-count gate — a missing Google average is no reason to drop the
  // reviews, because the rating is unpublished either way (SH-543). Built even
  // while `PUBLISH_GOOGLE_REVIEW_LIST` is off so the switch is the only thing
  // standing between the resolved reviews and the node.
  const review =
    reviews && reviews.summary.googleTotalReviewCount > 0
      ? buildReviewListJsonLd(mergeJsonLdReviewItems([], reviews.items))
      : [];

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': buildLocalBusinessJsonLdId(location, canonicalOrigin),
    'name': `Schülerhilfe ${resolveCenterName(location)}`.trim(),
    'url': buildLocalBusinessUrl(location, canonicalOrigin),
    ...(telephone ? { telephone } : {}),
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': location.street,
      'postalCode': location.zipCode,
      'addressLocality': location.cityName,
      ...(location.address?.state?.name
        ? { addressRegion: location.address.state.name }
        : {}),
      'addressCountry': location.address?.country ?? 'DE',
    },
    'geo': buildGeoJsonLd(location.latitude, location.longitude),
    ...(openingHours.length > 0 ? { openingHours } : {}),
    ...(priceRange ? { priceRange } : {}),
    ...(profile
      ? {
          hasOfferCatalog: buildOfferCatalogJsonLd(profile.offerCatalog),
          sameAs: profile.socialProfiles.map((p) => p.url),
        }
      : {}),
    ...(PUBLISH_GOOGLE_AGGREGATE_RATING && aggregateRating
      ? { aggregateRating }
      : {}),
    ...(PUBLISH_GOOGLE_REVIEW_LIST && review.length ? { review } : {}),
  };
}
